'use client';

// @spec developer-tools-rum-metrics
import { datadogRum } from '@datadog/browser-rum';
import { isRumInitialized } from './init-rum';
import { getRumLogger } from './rum-logger';

// ---------------------------------------------------------------------------
// Action vocabulary (literal string union — single source of truth)
// ---------------------------------------------------------------------------

export type RumActionName =
  | 'thread.create'
  | 'calendar.event.save'
  | 'daily-heads-up.generate'
  | 'auth.signin.start'
  | 'auth.signout'
  | 'auth.reauth.banner_shown'
  | 'settings.save'
  | 'heads_up.cache'
  | 'thread.load.cache'
  | 'chat.feedback.thumbs';

// ---------------------------------------------------------------------------
// Payload allow-list (TypeScript shape + runtime filter)
// ---------------------------------------------------------------------------
//
// TypeScript types describe the contract; the runtime filter ENFORCES it.
// `addAction(payload as any)` is one cast away from sneaking arbitrary data
// past the compiler, so the runtime guard is the actual PII guarantee.

const ALLOWED_KEYS = new Set<string>([
  // Opaque IDs — no semantic content
  'threadId',
  'eventId',
  'calendarId',
  'userId',
  'familyId',
  'commentId',
  // Booleans
  'ok',
  'isRecurring',
  'is_today',
  // Enum tags (string values are also enum-checked)
  'operation',
  'recurrenceTarget',
  'undoCapability',
  'mode',
  'method',
  'heads_up_cache',
  'threads_cache',
  'thread_count',
  'rating',
  // Counts
  'attendeeCount',
  'eventCount',
  'accountCount',
  // Date strings (YYYY-MM-DD; never timestamps with high cardinality)
  'date',
  'dateRange',
  // Reauth banner
  'bannerVariant',
]);

const ALLOWED_ENUM_VALUES: Record<string, ReadonlySet<string>> = {
  operation: new Set(['create', 'update', 'delete']),
  recurrenceTarget: new Set(['this_one', 'this_and_following', 'all']),
  undoCapability: new Set(['full', 'lossy', 'none']),
  mode: new Set(['cross-env', 'standard']),
  heads_up_cache: new Set(['hit', 'miss']),
  threads_cache: new Set(['hit', 'miss']),
  thread_count: new Set(['0', '1-3', '4-10', '10+']),
  bannerVariant: new Set(['token_failure', 'email_reconnect', 'account_mismatch']),
  rating: new Set(['up', 'down', 'clear']),
};

// `method` is intentionally NOT enum-checked — it carries a service-method
// name (e.g., `updateLocation`). The instrumentor that emits it constrains
// values via the `INSTRUMENTED_FAMILY_CONFIG_METHODS` constant; the runtime
// filter accepts any short, identifier-shaped string.
const METHOD_KEY = 'method';
const IDENTIFIER_RE = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;

// Date strings: pin to YYYY-MM-DD to keep cardinality low and prevent
// timestamp leakage. Any other string in `date`/`dateRange` is rejected.
const DATE_KEYS = new Set(['date', 'dateRange']);
const DATE_RE = /^\d{4}-\d{2}-\d{2}(\.\.\d{4}-\d{2}-\d{2})?$/;

// ---------------------------------------------------------------------------
// Typed payload shapes (compile-time hint; runtime filter is authoritative)
// ---------------------------------------------------------------------------

export interface RumActionPayload {
  threadId?: string;
  eventId?: string;
  calendarId?: string;
  userId?: string;
  familyId?: string;
  commentId?: string;
  ok?: boolean;
  isRecurring?: boolean;
  is_today?: boolean;
  operation?: 'create' | 'update' | 'delete';
  recurrenceTarget?: 'this_one' | 'this_and_following' | 'all';
  undoCapability?: 'full' | 'lossy' | 'none';
  mode?: 'cross-env' | 'standard';
  method?: string;
  heads_up_cache?: 'hit' | 'miss';
  threads_cache?: 'hit' | 'miss';
  thread_count?: '0' | '1-3' | '4-10' | '10+';
  attendeeCount?: number;
  eventCount?: number;
  accountCount?: number;
  date?: string;
  dateRange?: string;
  bannerVariant?: 'token_failure' | 'email_reconnect' | 'account_mismatch';
  rating?: 'up' | 'down' | 'clear';
}

// ---------------------------------------------------------------------------
// Filter
// ---------------------------------------------------------------------------

interface FilterContext {
  warn: (message: string, meta?: Record<string, unknown>) => void;
}

// Key-specific type expectations. The blanket "accept any boolean/number"
// gate that used to live here would have let
// `recordRumAction(..., { threadId: false })` or `{ operation: 1 }` slip
// through — violating the runtime contract. Validate the boolean and
// numeric keys against their actual key-specific shapes BEFORE the
// string branch.
const BOOLEAN_KEYS = new Set<string>(['ok', 'isRecurring', 'is_today']);
const NUMERIC_KEYS = new Set<string>(['attendeeCount', 'eventCount', 'accountCount']);

/** Strips disallowed keys and validates values against key-specific type and enum rules, emitting a logger warn for each rejected entry to surface misconfigured call sites without silently leaking PII. */
function filterPayload(raw: Record<string, unknown>, ctx: FilterContext): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!ALLOWED_KEYS.has(key)) {
      ctx.warn('rum_payload_rejected: unknown key', { key });
      continue;
    }
    if (BOOLEAN_KEYS.has(key)) {
      if (typeof value !== 'boolean') {
        ctx.warn('rum_payload_rejected: expected boolean', { key, type: typeof value });
        continue;
      }
      out[key] = value;
      continue;
    }
    if (NUMERIC_KEYS.has(key)) {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        ctx.warn('rum_payload_rejected: expected finite number', { key, type: typeof value });
        continue;
      }
      out[key] = value;
      continue;
    }
    if (typeof value !== 'string') {
      ctx.warn('rum_payload_rejected: unsupported value type', { key, type: typeof value });
      continue;
    }
    const enumSet = ALLOWED_ENUM_VALUES[key];
    if (enumSet != null) {
      if (!enumSet.has(value)) {
        ctx.warn('rum_payload_rejected: enum value not allowed', { key });
        continue;
      }
      out[key] = value;
      continue;
    }
    if (DATE_KEYS.has(key)) {
      if (!DATE_RE.test(value)) {
        ctx.warn('rum_payload_rejected: not a YYYY-MM-DD date', { key });
        continue;
      }
      out[key] = value;
      continue;
    }
    if (key === METHOD_KEY) {
      if (!IDENTIFIER_RE.test(value)) {
        ctx.warn('rum_payload_rejected: method not an identifier', { key });
        continue;
      }
      out[key] = value;
      continue;
    }
    // ID-shaped opaque strings: accept short ASCII identifiers, reject
    // anything containing the @-sign or whitespace (prevents email-shaped
    // values from sneaking in via misnamed payloads).
    let rejectReason: 'too-long' | 'at-sign' | 'whitespace' | null = null;
    if (value.length > 128) {
      rejectReason = 'too-long';
    } else if (value.includes('@')) {
      rejectReason = 'at-sign';
    } else if (/\s/.test(value)) {
      rejectReason = 'whitespace';
    }
    if (rejectReason != null) {
      ctx.warn('rum_payload_rejected: string fails opaque-id shape check', {
        key,
        reason: rejectReason,
      });
      continue;
    }
    out[key] = value;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

let stateOverride: { initialized: boolean } | null = null;

/** Returns whether RUM is currently initialized, honouring any test-injected state override before falling back to the real SDK flag. */
function initializedNow(): boolean {
  if (stateOverride != null) {
    return stateOverride.initialized;
  }
  return isRumInitialized();
}

/** Emits a named RUM custom action with a PII-filtered payload, dropping the call silently when RUM is not yet initialized. */
export function recordRumAction(name: RumActionName, payload: RumActionPayload): void {
  if (!initializedNow()) {
    return;
  }
  const filtered = filterPayload(payload as Record<string, unknown>, {
    warn: (msg, meta) => getRumLogger().warn(msg, meta),
  });
  datadogRum.addAction(name, filtered);
}

// ---------------------------------------------------------------------------
// Site-specific helpers — keep call sites clean and the wiring greppable
// ---------------------------------------------------------------------------

/** Emits a `thread.create` RUM action tagging the new thread by its opaque id, enabling per-session create-rate tracking in dashboards. */
export function recordThreadCreateAction(threadId: string): void {
  recordRumAction('thread.create', { threadId });
}

/** Emits a `chat.feedback.thumbs` RUM action tagged by thread/comment id and vote, so a thumbs vote can be joined against `MessageFeedback` rows for satisfaction analysis (FPO-3107). */
export function recordChatFeedbackAction(payload: {
  threadId: string;
  commentId: string;
  rating: 'up' | 'down' | 'clear';
}): void {
  recordRumAction('chat.feedback.thumbs', payload);
}

export interface CalendarSavePayload {
  operation: 'create' | 'update' | 'delete';
  recurrenceTarget?: 'this_one' | 'this_and_following' | 'all';
  undoCapability: 'full' | 'lossy' | 'none';
  eventId?: string;
  calendarId?: string;
}

/** Emits a `calendar.event.save` RUM action with the operation type, recurrence scope, and undo capability so calendar-save latency can be sliced by mutation shape in dashboards. */
export function recordCalendarSaveAction(payload: CalendarSavePayload): void {
  recordRumAction('calendar.event.save', payload);
}

/** Emits a `daily-heads-up.generate` RUM action so generate-request frequency and timing can be tracked per date in dashboards. */
export function recordHeadsUpGenerateAction(payload: { date: string }): void {
  recordRumAction('daily-heads-up.generate', payload);
}

/** Emits an `auth.signin.start` RUM action tagged with the sign-in mode so cross-env vs standard sign-in paths can be compared in funnel dashboards. */
export function recordSigninAction(mode: 'cross-env' | 'standard'): void {
  recordRumAction('auth.signin.start', { mode });
}

/** Emits an `auth.signout` RUM action so sign-out events appear as a discrete step in session replays and funnel dashboards. */
export function recordSignoutAction(): void {
  recordRumAction('auth.signout', {});
}

// FPO-806: paired with `addTiming('time_to_heads_up')` — emits the cache state
// as an event-scoped attribute instead of leaking onto session-global context.
// Joined with the timing in the dashboard via session id.
/** Emits a `heads_up.cache` RUM action paired with the `time_to_heads_up` timing so dashboards can split that metric by cache-hit vs cache-miss without polluting session-global context. */
export function recordHeadsUpCacheAction(cacheState: 'hit' | 'miss'): void {
  recordRumAction('heads_up.cache', { heads_up_cache: cacheState });
}

export interface ThreadLoadCachePayload {
  threads_cache: 'hit' | 'miss';
  heads_up_cache: 'hit' | 'miss';
  thread_count: '0' | '1-3' | '4-10' | '10+';
  is_today: boolean;
}

// FPO-921: paired with `addTiming('time_to_thread_loaded')` — carries the
// per-event dimensions for slicing TTL by cache state, payload size, and
// is-today vs other-day. Same event-scoped pattern as `heads_up.cache`.
/** Emits a `thread.load.cache` RUM action paired with the per-view `time_to_thread_loaded` timing so thread-load latency can be sliced by cache state, thread count, and is-today in dashboards. */
export function recordThreadLoadCacheAction(payload: ThreadLoadCachePayload): void {
  recordRumAction('thread.load.cache', payload);
}

export type ReauthBannerVariant = 'token_failure' | 'email_reconnect' | 'account_mismatch';

/** Emits an `auth.reauth.banner_shown` RUM action when the reauth banner renders, carrying the variant and affected-account count so Datadog dashboards can correlate user-perceived occurrences against server-side reauth marks. */
export function recordReauthBannerShownAction(
  variant: ReauthBannerVariant,
  accountCount: number
): void {
  recordRumAction('auth.reauth.banner_shown', { bannerVariant: variant, accountCount });
}

/** Injects or clears a test-only initialization override so tests can verify action emit/skip behaviour without touching the real Datadog SDK state. @visibleForTesting */
export function __resetRumActionStateForTests(state?: { initialized: boolean }): void {
  stateOverride = state ?? null;
}
